import React, { useState, useEffect } from 'react';

const PWAInstallPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [isAppInstalled, setIsAppInstalled] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        // Enhanced detection for installed PWA
        const checkIfInstalled = () => {
            // Check for standalone mode (PWA installed)
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                                window.navigator.standalone || 
                                document.referrer.includes('android-app://');
            
            // Check if running in PWA mode
            const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                         window.matchMedia('(display-mode: fullscreen)').matches ||
                         window.matchMedia('(display-mode: minimal-ui)').matches;
            
            return isStandalone || isPWA;
        };

        // Detect iOS devices
        const detectIOS = () => {
            return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        };

        setIsAppInstalled(checkIfInstalled());
        setIsIOS(detectIOS());

        // Check if user has dismissed the prompt recently (within 7 days)
        const dismissedTime = localStorage.getItem('pwa-install-dismissed');
        const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
        const recentlyDismissed = dismissedTime && parseInt(dismissedTime) > sevenDaysAgo;

        const handleBeforeInstallPrompt = (e) => {
            // Prevent the mini-info bar from appearing on mobile
            e.preventDefault();
            // Stash the event so it can be triggered later
            setDeferredPrompt(e);
            
            // Show our custom prompt only if:
            // 1. App is not already installed
            // 2. User hasn't dismissed recently
            // 3. Not on iOS (iOS doesn't support beforeinstallprompt)
            if (!checkIfInstalled() && !recentlyDismissed && !detectIOS()) {
                // Delay showing prompt by 3 seconds for better UX
                setTimeout(() => {
                    setShowPrompt(true);
                }, 3000);
            }
        };

        const handleAppInstalled = () => {
            setIsAppInstalled(true);
            setShowPrompt(false);
            console.log('PWA was installed successfully');
        };

        // For iOS devices, show manual install instructions
        if (detectIOS() && !checkIfInstalled() && !recentlyDismissed) {
            setTimeout(() => {
                setShowPrompt(true);
            }, 3000);
        }

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;
        
        // Hide our custom prompt
        setShowPrompt(false);
        
        try {
            // Show the install prompt
            await deferredPrompt.prompt();
            
            // Wait for the user to respond to the prompt
            const choiceResult = await deferredPrompt.userChoice;
            
            if (choiceResult.outcome === 'accepted') {
                console.log('User accepted the PWA installation');
                setIsAppInstalled(true);
            } else {
                console.log('User dismissed the PWA installation');
                // Remember dismissal for 7 days
                localStorage.setItem('pwa-install-dismissed', Date.now().toString());
            }
        } catch (error) {
            console.error('Error during PWA installation:', error);
        }
        
        // Clear the deferredPrompt
        setDeferredPrompt(null);
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        setDeferredPrompt(null);
        // Remember dismissal for 7 days
        localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    };

    // Don't show if app is installed
    if (isAppInstalled) return null;
    
    // Don't show if prompt is not active
    if (!showPrompt) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Install SDIDTK PWA
                    </h3>
                    
                    {isIOS ? (
                        <div className="text-left">
                            <p className="text-gray-600 mb-4">
                                Install aplikasi SDIDTK di iPhone/iPad Anda untuk pengalaman yang lebih baik:
                            </p>
                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                <ol className="text-sm text-gray-700 space-y-2">
                                    <li className="flex items-start">
                                        <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">1</span>
                                        Tap tombol <strong>Share</strong> di Safari
                                    </li>
                                    <li className="flex items-start">
                                        <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">2</span>
                                        Pilih <strong>"Add to Home Screen"</strong>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">3</span>
                                        Tap <strong>"Add"</strong> untuk menginstall
                                    </li>
                                </ol>
                            </div>
                        </div>
                    ) : (
                        <p className="text-gray-600 mb-6">
                            Install aplikasi SDIDTK di perangkat Anda untuk akses yang lebih cepat dan pengalaman yang lebih baik, bahkan saat offline!
                        </p>
                    )}
                    
                    <div className="flex gap-3">
                        <button
                            onClick={handleDismiss}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            {isIOS ? 'Mengerti' : 'Nanti Saja'}
                        </button>
                        {!isIOS && (
                            <button
                                onClick={handleInstallClick}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Install
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PWAInstallPrompt;
